import {
  findPageFixtures,
  getPageFixture,
  MATRIX_LOCALES,
  type MatrixLocale,
} from '@web-studio/core-fixtures';
import {
  buildTierPreview,
  type TierBlockRow,
} from '@web-studio/layout-matrix';
import {
  getColorSetsForTheme,
  PACKAGE_TIER_KEY_VALUES,
  PACKAGE_TIER_KEYS,
  PLATFORM_VERTICAL_KEY_VALUES,
  PLATFORM_VERTICAL_KEYS,
  SPACING_ENUM_KEY_VALUES,
  SPACING_ENUM_KEYS,
  THEME_REGISTRY,
  type PackageTierKey,
  type PlatformVerticalKey,
  type SpacingEnumKey,
} from '@web-studio/shared-schemas';
import { useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LabModeBar } from '@/components/LabModeBar';
import { MatrixCanvas } from '@/components/MatrixCanvas';
import { MatrixWorkbench } from '@/components/MatrixWorkbench';
import { useSeededBlocksEditor } from '@/hooks/useFixtureBlocks';
import { useLabCaptureMode } from '@/hooks/useLabCaptureMode';
import { parseLabChromeParams } from '@/lib/matrixState';

/**
 * Package layout — merged former Page lab + Tier matrix.
 * Vertical × package preview, allowed-block inventory, and engineer tooling.
 */
export function TierMatrixPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const captureMode = useLabCaptureMode();
  const chrome = useMemo(() => parseLabChromeParams(searchParams), [searchParams]);

  const vertical = (PLATFORM_VERTICAL_KEY_VALUES.includes(
    searchParams.get('vertical') as PlatformVerticalKey,
  )
    ? searchParams.get('vertical')
    : PLATFORM_VERTICAL_KEYS.restaurant) as PlatformVerticalKey;

  const tier = (PACKAGE_TIER_KEY_VALUES.includes(searchParams.get('tier') as PackageTierKey)
    ? searchParams.get('tier')
    : PACKAGE_TIER_KEYS.recommended) as PackageTierKey;

  const layoutFixtures = useMemo(() => findPageFixtures(vertical, tier), [vertical, tier]);
  const preview = useMemo(() => buildTierPreview(vertical, tier), [vertical, tier]);

  const pageFixtureId =
    searchParams.get('page') && layoutFixtures.some((f) => f.id === searchParams.get('page'))
      ? (searchParams.get('page') as string)
      : (layoutFixtures[0]?.id ?? '');

  const pageDef = pageFixtureId ? getPageFixture(pageFixtureId) : undefined;
  const activeLayout = layoutFixtures.find((f) => f.id === pageFixtureId);
  const themeId =
    searchParams.get('theme') ?? activeLayout?.defaultThemeId ?? preview.defaultThemeId;
  const colorsets = getColorSetsForTheme(themeId);
  const colorsetId = searchParams.get('colorset') ?? colorsets[0]?.id ?? '';

  const spacingParam = searchParams.get('spacing');
  const spacingEnum = (
    SPACING_ENUM_KEY_VALUES.includes(spacingParam as SpacingEnumKey)
      ? spacingParam
      : SPACING_ENUM_KEYS.balanced
  ) as SpacingEnumKey;

  const localeParam = searchParams.get('locale');
  const locale = (
    MATRIX_LOCALES.includes(localeParam as MatrixLocale) ? localeParam : 'en'
  ) as MatrixLocale;

  const viewportParam = searchParams.get('viewport');
  const viewport =
    viewportParam === 'mobile' || viewportParam === 'tablet' ? viewportParam : 'desktop';

  const seedBlocks = useMemo(
    () => pageDef?.blocks ?? preview.previewBlocks,
    [pageDef, preview.previewBlocks],
  );
  const editorKey = pageFixtureId || `preview:${vertical}:${tier}`;
  const { blocks, patchBlockContent, patchBlockMedia, reset } = useSeededBlocksEditor(
    editorKey,
    seedBlocks,
  );

  const canvasBlocks = blocks.length > 0 ? blocks : seedBlocks;
  const showEngineerChrome = canvasBlocks.length > 0;

  const updateParams = useCallback(
    (patch: Record<string, string | null>) => {
      const p = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value === null || value === '') p.delete(key);
        else p.set(key, value);
      }
      setSearchParams(p, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setVerticalTier = (nextVertical: PlatformVerticalKey, nextTier: PackageTierKey) => {
    const fixtures = findPageFixtures(nextVertical, nextTier);
    const nextPreview = buildTierPreview(nextVertical, nextTier);
    const nextPage = fixtures[0];
    const nextTheme = nextPage?.defaultThemeId ?? nextPreview.defaultThemeId;
    const sets = getColorSetsForTheme(nextTheme);
    updateParams({
      vertical: nextVertical,
      tier: nextTier,
      page: nextPage?.id ?? null,
      theme: nextTheme,
      colorset: sets[0]?.id ?? null,
    });
  };

  const syncChrome = (lab: Partial<typeof chrome>) => {
    const next = { ...chrome, ...lab };
    updateParams({
      mode: next.engineer ? 'engineer' : null,
      targets: next.editTargets ? '1' : null,
      parity: next.parity ? '1' : null,
    });
  };

  const implementedCount = preview.rows.filter((r) => r.tierAccess === 'active').length;
  const registryOnlyCount = preview.rows.filter((r) => r.tierAccess === 'registry_only').length;

  if (captureMode) {
    return canvasBlocks.length > 0 ? (
      <div className="lab-capture-stage">
        <MatrixCanvas
          blocks={canvasBlocks}
          locale={locale}
          themeId={themeId}
          colorsetId={colorsetId}
          spacingEnum={spacingEnum}
          viewport={viewport}
        />
      </div>
    ) : (
      <p className="text-sm" style={{ color: 'var(--lab-muted)' }}>
        No implemented blocks for this package yet.
      </p>
    );
  }

  return (
    <div className="lab-page">
      <header className="lab-page-header">
        <h1 className="lab-page-header__title">Package layout</h1>
        <p className="lab-page-header__desc">
          Vertical × package composition · {implementedCount} blocks renderable ·{' '}
          {registryOnlyCount} registry-only
          {showEngineerChrome ? ' · click-to-edit + fixture inspector' : ''}
        </p>
      </header>

      <div className="lab-toolbar">
        <div className="lab-field">
          <label htmlFor="pkg-vertical">Vertical</label>
          <select
            id="pkg-vertical"
            value={vertical}
            onChange={(e) => setVerticalTier(e.target.value as PlatformVerticalKey, tier)}
          >
            {PLATFORM_VERTICAL_KEY_VALUES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="lab-field">
          <label htmlFor="pkg-tier">Package</label>
          <select
            id="pkg-tier"
            value={tier}
            onChange={(e) => setVerticalTier(vertical, e.target.value as PackageTierKey)}
          >
            {PACKAGE_TIER_KEY_VALUES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        {layoutFixtures.length > 0 ? (
          <div className="lab-field">
            <label htmlFor="pkg-layout">Layout fixture</label>
            <select
              id="pkg-layout"
              value={pageFixtureId}
              onChange={(e) => {
                const next = layoutFixtures.find((f) => f.id === e.target.value);
                const nextTheme = next?.defaultThemeId ?? themeId;
                const sets = getColorSetsForTheme(nextTheme);
                updateParams({
                  page: e.target.value,
                  theme: nextTheme,
                  colorset: sets[0]?.id ?? colorsetId,
                });
              }}
            >
              {layoutFixtures.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="lab-field">
          <label htmlFor="pkg-theme">Theme</label>
          <select
            id="pkg-theme"
            value={themeId}
            onChange={(e) => {
              const sets = getColorSetsForTheme(e.target.value);
              updateParams({
                theme: e.target.value,
                colorset: sets[0]?.id ?? colorsetId,
              });
            }}
          >
            {Object.values(THEME_REGISTRY).map((t) => (
              <option key={t.themeId} value={t.themeId}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="lab-field">
          <label htmlFor="pkg-colorset">Colorset</label>
          <select
            id="pkg-colorset"
            value={colorsetId}
            onChange={(e) => updateParams({ colorset: e.target.value })}
          >
            {colorsets.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="lab-field">
          <label htmlFor="pkg-spacing">Spacing</label>
          <select
            id="pkg-spacing"
            value={spacingEnum}
            onChange={(e) => updateParams({ spacing: e.target.value })}
          >
            {SPACING_ENUM_KEY_VALUES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="lab-field">
          <label htmlFor="pkg-locale">Locale</label>
          <select
            id="pkg-locale"
            value={locale}
            onChange={(e) => updateParams({ locale: e.target.value })}
          >
            {MATRIX_LOCALES.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div className="lab-field">
          <label htmlFor="pkg-viewport">Viewport</label>
          <select
            id="pkg-viewport"
            value={viewport}
            onChange={(e) =>
              updateParams({
                viewport: e.target.value === 'desktop' ? null : e.target.value,
              })
            }
          >
            <option value="desktop">Desktop</option>
            <option value="tablet">Tablet</option>
            <option value="mobile">Mobile</option>
          </select>
        </div>
      </div>

      <PackageInventory rows={preview.rows} />

      {showEngineerChrome ? (
        <LabModeBar
          engineerMode={chrome.engineer}
          showEditTargets={chrome.editTargets}
          showParity={chrome.parity}
          onEngineerModeChange={(engineer) =>
            syncChrome({
              engineer,
              parity: engineer ? chrome.parity : false,
            })
          }
          onShowEditTargetsChange={(editTargets) => syncChrome({ editTargets })}
          onShowParityChange={(parity) => syncChrome({ parity })}
          onExportJson={() => {
            const payload = canvasBlocks.length === 1 ? canvasBlocks[0] : canvasBlocks;
            const name =
              canvasBlocks.length === 1
                ? canvasBlocks[0]!.componentType
                : `${vertical}-${tier}-layout`;
            const blob = new Blob([JSON.stringify(payload, null, 2)], {
              type: 'application/json',
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${name}-fixture.json`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          onResetFixture={reset}
          exportDisabled={canvasBlocks.length === 0}
          resetDisabled={canvasBlocks.length === 0}
        />
      ) : null}

      {canvasBlocks.length > 0 ? (
        <MatrixWorkbench
          blocks={canvasBlocks}
          locale={locale}
          themeId={themeId}
          colorsetId={colorsetId}
          spacingEnum={spacingEnum}
          viewport={viewport}
          engineerMode={chrome.engineer}
          showEditTargets={chrome.editTargets}
          showParity={chrome.parity}
          onContentChange={(blockId, fieldKey, loc, value) =>
            patchBlockContent(blockId, fieldKey, loc, value)
          }
          onMediaChange={(blockId, fieldKey, value) => patchBlockMedia(blockId, fieldKey, value)}
          onShowParityChange={(parity) => syncChrome({ parity })}
        />
      ) : (
        <p className="text-sm" style={{ color: 'var(--lab-muted)' }}>
          No implemented blocks for this package yet.
        </p>
      )}
    </div>
  );
}

function PackageInventory({ rows }: { rows: TierBlockRow[] }) {
  return (
    <section className="lab-package-meta" aria-label="Allowed blocks">
      <div className="lab-package-meta__allowed">
        <span className="lab-package-meta__label">Allowed blocks</span>
        <div className="lab-package-meta__chips">
          {rows.map((row) => {
            const badgeClass =
              row.tierAccess === 'active'
                ? 'lab-badge lab-badge--ok'
                : row.tierAccess === 'registry_only'
                  ? 'lab-badge lab-badge--warn'
                  : 'lab-badge lab-badge--muted';

            if (row.componentType) {
              return (
                <Link
                  key={row.registryKey}
                  to={`/matrix/block/${row.componentType}`}
                  className={`${badgeClass} no-underline`}
                  title={`${row.registryKey} · ${row.tierAccess} · open isolate`}
                >
                  {row.registryKey}
                </Link>
              );
            }

            return (
              <span
                key={row.registryKey}
                className={badgeClass}
                title={`${row.registryKey} · ${row.tierAccess}`}
              >
                {row.registryKey}
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}
